// ─────────────────────────────────────────────────────────────
// KanbanView — 3-column sprint board with drag-and-drop.
// Columns: Candidate Pool → Committed → Done
//
// Uses @dnd-kit for accessible, pointer+keyboard drag-and-drop.
// ─────────────────────────────────────────────────────────────

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Stack,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import type { FeatureCard, KanbanColumn as KanbanColumnType, KanbanBoard } from "../types";
import { kanbanApi, cardsApi, sprintsApi } from "../api/client";
import { useApi } from "../hooks/useApi";
import { TagList, PriorityDot, PriorityBadge, SubmittedBy } from "../components/CardBadges";
import { CardDetailDialog } from "../components/CardDetailDialog";
import { COLUMN_COLORS } from "../theme";

const COLUMNS: KanbanColumnType[] = [
  "Candidate Pool",
  "Committed",
  "Done",
];

interface KanbanViewProps {
  /**
   * Optional hint set by other views (e.g. HistoryView's "Open in Board" button).
   * When provided, KanbanView pre-selects this sprint and treats it as the
   * initialised default. Consumed once and then cleared by the parent via
   * onSprintHintConsumed so user-driven selection isn't overridden later.
   */
  sprintIdHint?: number;
  onSprintHintConsumed?: () => void;
}

export function KanbanView({ sprintIdHint, onSprintHintConsumed }: KanbanViewProps = {}) {
  const [selectedCard, setSelectedCard] = useState<FeatureCard | null>(null);
  const [activeCard, setActiveCard] = useState<FeatureCard | null>(null);
  // undefined = no sprint selected yet (waiting for sprint list to load)
  const [sprintId, setSprintId] = useState<number | undefined>(sprintIdHint);
  const [sprintIdInitialised, setSprintIdInitialised] = useState(sprintIdHint !== undefined);
  const [localColumns, setLocalColumns] = useState<Record<KanbanColumnType, FeatureCard[]> | null>(null);

  // Sprint queue-transition busy + error state. Greg's 2026-05-15
  // policy removed the admin-password confirmation dialogs that used to
  // gate Queue / Start / Un-queue — actions now fire directly on click,
  // the JWT gates the request, and failures surface inline. A single
  // busy flag is enough because the header only shows ONE transition
  // button at a time (the visible one depends on sprint_status).
  const [sprintActionBusy, setSprintActionBusy] = useState(false);
  const [sprintActionError, setSprintActionError] = useState<string | null>(null);
  // Create sprint dialog state
  const [createSprintOpen, setCreateSprintOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // Run a sprint queue transition. Wraps API call with busy + error
  // handling so the three button-click handlers below stay declarative.
  const runSprintTransition = useCallback(
    async (
      action: () => Promise<unknown>,
      successMessage: string,
      failurePrefix: string,
      onSuccess?: () => void,
    ) => {
      setSprintActionBusy(true);
      setSprintActionError(null);
      try {
        await action();
        setSnackbarMessage(successMessage);
        onSuccess?.();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Unknown error";
        setSprintActionError(`${failurePrefix}: ${msg}`);
      } finally {
        setSprintActionBusy(false);
      }
    },
    [],
  );

  // Backlog side panel state
  const [backlogPanelOpen, setBacklogPanelOpen] = useState(false);
  const [backlogCards, setBacklogCards] = useState<FeatureCard[]>([]);
  const [backlogSearch, setBacklogSearch] = useState("");

  const { data, loading, error, refetch } = useApi<KanbanBoard>(
    // Only fetch once a sprint is selected — avoids the old "active sprint" fallback
    () => sprintId !== undefined ? kanbanApi.board(sprintId) : Promise.resolve(null as unknown as KanbanBoard),
    [sprintId]
  );

  // Sprints for the selector dropdown — planning sprints (editable) and active
  // sprints (read-only view). Queued and completed sprints live in the Sprints tab.
  const { data: planningSprints, refetch: refetchPlanning } = useApi(
    () => sprintsApi.list("planning"),
    []
  );
  const { data: activeSprints, refetch: refetchActive } = useApi(
    () => sprintsApi.list("active"),
    []
  );

  // Merge: planning first so new sprints default to planning; active appended at end
  const sprints = [...(planningSprints ?? []), ...(activeSprints ?? [])];

  const refetchSprints = useCallback(() => {
    refetchPlanning();
    refetchActive();
  }, [refetchPlanning, refetchActive]);

  // Consume a fresh sprintIdHint from the parent. Runs whenever the hint
  // changes — so HistoryView's "Open in Board" can switch the selected
  // sprint even if KanbanView is already mounted. We notify the parent via
  // onSprintHintConsumed so it can clear the hint and won't re-apply it.
  useEffect(() => {
    if (sprintIdHint === undefined) return;
    setSprintId(sprintIdHint);
    setSprintIdInitialised(true);
    onSprintHintConsumed?.();
  }, [sprintIdHint, onSprintHintConsumed]);

  // Once the sprint list loads, pick the first planning sprint as default.
  // Fall back to the first active sprint if no planning sprints exist.
  useEffect(() => {
    if (sprintIdInitialised) return;
    if (sprints.length === 0) return;
    setSprintId(sprints[0].id);
    setSprintIdInitialised(true);
  }, [sprints, sprintIdInitialised]);

  // Sync local state from fetched data
  useEffect(() => {
    if (data) setLocalColumns(data.columns);
  }, [data]);

  // Fetch backlog cards when the panel opens
  useEffect(() => {
    if (!backlogPanelOpen) return;
    void (async () => {
      try {
        const result = await cardsApi.list({ limit: 200, sort_by: "priority", order: "desc" });
        setBacklogCards(result.cards);
      } catch {
        // silently ignore — panel will show empty
      }
    })();
  }, [backlogPanelOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor)
  );

  // Sprint is read-only when queued or active — no drag-and-drop allowed
  const isReadOnly = data?.sprint_status === "queued" || data?.sprint_status === "active";

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      // Board is locked for queued sprints — do not allow drag initiation
      if (isReadOnly) return;
      if (!localColumns) return;
      const cardId = Number(event.active.id);
      for (const col of COLUMNS) {
        const found = localColumns[col].find((c) => c.id === cardId);
        if (found) {
          setActiveCard(found);
          return;
        }
      }
    },
    [localColumns, isReadOnly]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveCard(null);
      // Board is locked for queued sprints — ignore all drag events
      if (isReadOnly) return;
      if (!localColumns || !event.over) return;

      const cardId = Number(event.active.id);

      // event.over.id may be a card ID (number string) or a column name
      let targetColumn = event.over.id as KanbanColumnType;
      if (!COLUMNS.includes(targetColumn)) {
        // Dropped onto a card — find which column it belongs to
        const targetCardId = Number(event.over.id);
        let found = false;
        for (const col of COLUMNS) {
          if (localColumns[col]?.find((c) => c.id === targetCardId)) {
            targetColumn = col;
            found = true;
            break;
          }
        }
        if (!found) return; // safety: can't resolve target, abort
      }

      // Done column is locked — only Paul can move items there
      if (targetColumn === "Done") {
        setSnackbarMessage("Only Paul can mark items as done.");
        return;
      }

      // Find which column the card is currently in
      let sourceColumn: KanbanColumnType | null = null;
      let card: FeatureCard | null = null;
      for (const col of COLUMNS) {
        const found = localColumns[col].find((c) => c.id === cardId);
        if (found) {
          sourceColumn = col;
          card = found;
          break;
        }
      }

      if (!sourceColumn || !card || sourceColumn === targetColumn) return;

      // Optimistic update — move card locally
      setLocalColumns((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next[sourceColumn!] = next[sourceColumn!].filter((c) => c.id !== cardId);
        next[targetColumn] = [{ ...card!, kanban_column: targetColumn }, ...next[targetColumn]];
        return next;
      });

      // Persist to backend
      try {
        await cardsApi.move(cardId, targetColumn, data?.sprint_id ?? undefined);
      } catch {
        // Revert on failure
        refetch();
      }
    },
    [localColumns, data?.sprint_id, isReadOnly, refetch]
  );

  const handleCardUpdated = useCallback(
    (updated: FeatureCard) => {
      setSelectedCard(updated);
      refetch();
    },
    [refetch]
  );

  const handleMoveToBacklog = useCallback(
    async (card: FeatureCard) => {
      if (!data?.sprint_id) return;
      try {
        const fullCard = await cardsApi.get(card.id);
        const sprintItem = fullCard.sprint_items?.[0];
        if (!sprintItem) {
          setSnackbarMessage("Could not find sprint item for this card");
          return;
        }
        await sprintsApi.removeItem(data.sprint_id, sprintItem.id);
        refetch();
        setSnackbarMessage(`Moved "${card.title}" back to backlog`);
      } catch (err) {
        setSnackbarMessage(err instanceof Error ? err.message : "Failed to move card to backlog");
      }
    },
    [data?.sprint_id, refetch]
  );

  const columnData = localColumns ?? (data?.columns ?? null);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h2" sx={{ mr: "auto" }}>
          Sprint Board
          {data?.sprint_name && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {data.sprint_name}
              {data.sprint_description ? ` — ${data.sprint_description}` : ""}
            </Typography>
          )}
        </Typography>

        {/* Create Sprint button — always visible in header */}
        <Tooltip title="Create a new planning sprint">
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutlineIcon fontSize="small" />}
            onClick={() => setCreateSprintOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Create Sprint
          </Button>
        </Tooltip>

        {/* Queue Sprint button — only shown for planning sprints */}
        {data?.sprint_status === "planning" && data.sprint_id != null && (
          <Tooltip title="Queue this sprint for Paul to execute">
            <span>
              {/* span wrapper keeps the Tooltip working while the Button
                  is disabled mid-request (MUI requirement). */}
              <Button
                variant="contained"
                size="small"
                disabled={sprintActionBusy}
                startIcon={
                  sprintActionBusy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon fontSize="small" />
                  )
                }
                onClick={() => {
                  const id = data.sprint_id;
                  if (id == null) return;
                  void runSprintTransition(
                    () => sprintsApi.confirm(id),
                    "Sprint queued for Paul",
                    "Failed to queue sprint",
                    () => {
                      // Sprint moved to queued — drop selection, re-init,
                      // and refetch so the selector updates.
                      setSprintId(undefined);
                      setSprintIdInitialised(false);
                      refetchSprints();
                      refetch();
                    },
                  );
                }}
                sx={{
                  backgroundColor: "success.main",
                  color: "#fff",
                  "&:hover": { backgroundColor: "success.dark" },
                  flexShrink: 0,
                }}
              >
                Queue Sprint
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Start Sprint button — only shown for queued sprints */}
        {data?.sprint_status === "queued" && data.sprint_id != null && (
          <Tooltip title="Mark this sprint as active — Paul will start working on it">
            <span>
              <Button
                variant="contained"
                size="small"
                disabled={sprintActionBusy}
                startIcon={
                  sprintActionBusy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon fontSize="small" />
                  )
                }
                onClick={() => {
                  const id = data.sprint_id;
                  if (id == null) return;
                  void runSprintTransition(
                    () => sprintsApi.start(id),
                    "Sprint started — Paul is now working on it",
                    "Failed to start sprint",
                    () => {
                      refetchSprints();
                      refetch();
                    },
                  );
                }}
                sx={{
                  backgroundColor: "success.main",
                  color: "#fff",
                  "&:hover": { backgroundColor: "success.dark" },
                  flexShrink: 0,
                }}
              >
                Start Sprint
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Un-queue button — only shown for queued sprints (not active) */}
        {data?.sprint_status === "queued" && data.sprint_id != null && (
          <Tooltip title="Move sprint back to planning">
            <span>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                disabled={sprintActionBusy}
                startIcon={
                  sprintActionBusy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RemoveCircleOutlineIcon fontSize="small" />
                  )
                }
                onClick={() => {
                  const id = data.sprint_id;
                  if (id == null) return;
                  void runSprintTransition(
                    () => sprintsApi.unqueue(id),
                    "Sprint moved back to planning",
                    "Failed to un-queue sprint",
                    () => {
                      // Sprint is back to planning — refetch so it
                      // reappears in the selector + board updates.
                      refetchSprints();
                      refetch();
                    },
                  );
                }}
                sx={{ flexShrink: 0 }}
              >
                Un-queue
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Sprint selector — planning and active sprints */}
        {sprints.length > 0 && sprintId !== undefined && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={sprintId}
              onChange={(e) => setSprintId(Number(e.target.value))}
            >
              {sprints.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.status === "active" ? `[Active] ${s.name}` : s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Tooltip title="Refresh board">
          <IconButton size="small" onClick={refetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status indicators */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load kanban board: {error}
        </Alert>
      )}

      {/* Read-only banner — shown when sprint is queued or active */}
      {!loading && !error && isReadOnly && (
        <Box
          sx={{
            px: 3,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor:
              data?.sprint_status === "active" ? "success.main" : "action.selected",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {data?.sprint_status === "active" ? (
              <AutorenewIcon
                sx={{ fontSize: "0.875rem", color: "#fff" }}
              />
            ) : (
              <LockOutlinedIcon sx={{ fontSize: "0.875rem", color: "text.secondary" }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color:
                  data?.sprint_status === "active" ? "#fff" : "text.secondary",
              }}
            >
              {data?.sprint_status === "active"
                ? "Sprint active — Paul is working on it"
                : "Sprint queued for Paul — read only"}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Empty board — no planning or active sprints exist */}
      {!loading && sprints.length === 0 && sprintId === undefined && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            opacity: 0.85,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No planning sprints
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Create one to get started.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateSprintOpen(true)}
          >
            Create Sprint
          </Button>
        </Box>
      )}

      {/* Board */}
      {!loading && !error && columnData && (
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                gap: 0,
                px: 2,
                py: 2,
                alignItems: "flex-start",
              }}
            >
              {COLUMNS.map((column) => (
                <ColumnPanel
                  key={column}
                  column={column}
                  cards={columnData[column] ?? []}
                  onCardClick={setSelectedCard}
                  isReadOnly={isReadOnly}
                  onMoveToBacklog={handleMoveToBacklog}
                />
              ))}
            </Box>

            <DragOverlay>
              {activeCard ? (
                <KanbanCardDragOverlay card={activeCard} />
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* VS Code-style edge tab — only shown for planning sprints */}
          {data?.sprint_status === "planning" && (
            <Box
              onClick={() => setBacklogPanelOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              aria-label={backlogPanelOpen ? "Close backlog panel" : "Open backlog panel"}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setBacklogPanelOpen((prev) => !prev);
                }
              }}
              sx={{
                width: "24px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "action.hover",
                borderLeft: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
                userSelect: "none",
                "&:hover": { backgroundColor: "action.selected" },
                transition: "background-color 150ms",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "-2px",
                },
              }}
            >
              {backlogPanelOpen ? (
                <ChevronRightIcon sx={{ fontSize: "small", color: "text.secondary" }} />
              ) : (
                <ChevronLeftIcon sx={{ fontSize: "small", color: "text.secondary" }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  transform: "rotate(180deg)",
                  letterSpacing: "0.08em",
                  color: "text.secondary",
                  mt: 0.5,
                  fontSize: "0.6rem",
                }}
              >
                BACKLOG
              </Typography>
            </Box>
          )}

          {/* Backlog side panel */}
          {backlogPanelOpen && data?.sprint_status === "planning" && (
            <BacklogPanel
              cards={backlogCards}
              search={backlogSearch}
              onSearchChange={setBacklogSearch}
              onClose={() => setBacklogPanelOpen(false)}
              columnData={columnData}
              sprintId={data?.sprint_id ?? null}
              onAdded={(title) => {
                setSnackbarMessage(`Added "${title}" to sprint`);
                refetch();
                // Re-fetch backlog list so panel hides the newly-added card
                void cardsApi.list({ limit: 200, sort_by: "priority", order: "desc" }).then((r) => setBacklogCards(r.cards));
              }}
            />
          )}
        </Box>
      )}

      {selectedCard && (
        <CardDetailDialog
          card={selectedCard}
          open={Boolean(selectedCard)}
          onClose={() => setSelectedCard(null)}
          onUpdated={handleCardUpdated}
        />
      )}

      {/* Create Sprint dialog */}
      <CreateSprintDialog
        open={createSprintOpen}
        onClose={() => setCreateSprintOpen(false)}
        onCreated={(newSprintId) => {
          setCreateSprintOpen(false);
          setSnackbarMessage("Sprint created successfully");
          // Select the new sprint and re-initialise the default selection next time
          setSprintId(newSprintId);
          setSprintIdInitialised(true);
          refetchSprints();
          refetch();
        }}
      />

      {/* Greg's 2026-05-15 policy: admin JWT is sufficient for sprint
          queue transitions — the three password-dialog instances that
          used to live here (Queue / Un-queue / Start) were removed. The
          header buttons fire the API directly via `runSprintTransition`,
          a single busy flag dims them mid-request, and failures surface
          in the Snackbar below. `QueueSprintDialog`, `UnqueueSprintDialog`,
          and `StartSprintDialog` definitions are retained as dead code
          for now in case a non-password confirmation step is reintroduced.
       */}

      {/* Action error from a sprint transition. Surfaced as a transient
          Snackbar so it doesn't reflow the header. */}
      <Snackbar
        open={sprintActionError !== null}
        autoHideDuration={6000}
        onClose={() => setSprintActionError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSprintActionError(null)}
          sx={{ width: "100%" }}
        >
          {sprintActionError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

// ── ColumnPanel ──────────────────────────────────────────────

interface ColumnPanelProps {
  column: KanbanColumnType;
  cards: FeatureCard[];
  onCardClick: (card: FeatureCard) => void;
  isReadOnly?: boolean;
  onMoveToBacklog: (card: FeatureCard) => void;
}

function ColumnPanel({ column, cards, onCardClick, isReadOnly = false, onMoveToBacklog }: ColumnPanelProps) {
  const accentColor = COLUMN_COLORS[column] ?? "#607d8b";
  const isDoneColumn = column === "Done";
  const cardIds = useMemo(() => cards.map((c) => String(c.id)), [cards]);
  const { setNodeRef, isOver } = useDroppable({ id: column });

  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 200,
        maxWidth: 320,
        display: "flex",
        flexDirection: "column",
        mx: 0.75,
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 14,
            borderRadius: "2px",
            backgroundColor: accentColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="overline"
          sx={{ color: accentColor, fontSize: "0.6875rem", lineHeight: 1.2 }}
          noWrap
        >
          {column}
        </Typography>
        {isDoneColumn && (
          <Tooltip title="Items are moved here by Paul when completed">
            <LockOutlinedIcon sx={{ fontSize: "0.75rem", color: "text.disabled", ml: 0.25 }} />
          </Tooltip>
        )}
        <Chip
          label={cards.length}
          size="small"
          sx={{
            height: "17px",
            fontSize: "0.6rem",
            backgroundColor: accentColor,
            color: "#fff",
            borderRadius: "3px",
          }}
        />
      </Box>

      {/* Drop zone */}
      <SortableContext id={column} items={cardIds} strategy={verticalListSortingStrategy}>
        <Box
          ref={setNodeRef}
          id={column}
          data-column={column}
          sx={{
            flex: 1,
            minHeight: 120,
            borderRadius: "4px",
            border: "1px dashed",
            borderColor: isOver ? accentColor : "divider",
            p: 0.75,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            transition: "background-color 150ms, border-color 150ms",
            backgroundColor: isOver ? `${accentColor}14` : "transparent",
          }}
        >
          {cards.map((card) => (
            <SortableKanbanCard
              key={card.id}
              card={card}
              onClick={() => onCardClick(card)}
              isReadOnly={isReadOnly}
              onMoveToBacklog={onMoveToBacklog}
            />
          ))}
          {cards.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 60,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>
                Drop cards here
              </Typography>
            </Box>
          )}
        </Box>
      </SortableContext>
    </Box>
  );
}

// ── SortableKanbanCard ────────────────────────────────────────

interface SortableKanbanCardProps {
  card: FeatureCard;
  onClick: () => void;
  isReadOnly?: boolean;
  onMoveToBacklog: (card: FeatureCard) => void;
}

function SortableKanbanCard({ card, onClick, isReadOnly = false, onMoveToBacklog }: SortableKanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(card.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        cursor: "pointer",
        "&:hover": { borderColor: "primary.main" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "1px",
        },
      }}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      onClick={onClick}
    >
      {/* Drag handle + title row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        {/* Drag handle — hidden when board is read-only */}
        {!isReadOnly && (
          <Box
            {...listeners}
            {...attributes}
            onClick={(e) => e.stopPropagation()}
            sx={{
              cursor: "grab",
              color: "text.secondary",
              opacity: 0.5,
              "&:hover": { opacity: 1 },
              mt: "-1px",
              flexShrink: 0,
            }}
            aria-label="Drag card"
          >
            <DragIndicatorIcon sx={{ fontSize: "0.875rem" }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <PriorityDot priority={card.priority} />
            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }} noWrap>
              {card.title}
            </Typography>
            {!isReadOnly && (
              <Tooltip title="Move back to backlog">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToBacklog(card);
                  }}
                  sx={{
                    p: "2px",
                    ml: "auto",
                    flexShrink: 0,
                    color: "text.disabled",
                    "&:hover": { color: "warning.main" },
                  }}
                  aria-label="Move card back to backlog"
                >
                  <UndoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {card.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {card.description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer badges */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.75,
          flexWrap: "wrap",
        }}
      >
        {card.tags.length > 0 && <TagList tags={card.tags} max={2} />}
        <Box sx={{ ml: "auto", display: "flex", gap: 0.5, alignItems: "center" }}>
          <SubmittedBy name={card.submitted_by} />
          <PriorityBadge priority={card.priority ?? "Medium"} />
        </Box>
      </Box>

      {card.agent_assignment && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, opacity: 0.7 }}
        >
          {card.agent_assignment}
        </Typography>
      )}
    </Paper>
  );
}

// ── BacklogPanel ──────────────────────────────────────────────

interface BacklogPanelProps {
  cards: FeatureCard[];
  search: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  columnData: Record<KanbanColumnType, FeatureCard[]>;
  sprintId: number | null;
  onAdded: (title: string) => void;
}

function BacklogPanel({
  cards,
  search,
  onSearchChange,
  onClose,
  columnData,
  sprintId,
  onAdded,
}: BacklogPanelProps) {
  const [addingId, setAddingId] = useState<number | null>(null);

  // IDs of cards already on the board
  const boardCardIds = useMemo(() => {
    const ids = new Set<number>();
    for (const col of COLUMNS) {
      for (const c of (columnData[col] ?? [])) {
        ids.add(c.id);
      }
    }
    return ids;
  }, [columnData]);

  const visibleCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter(
      (c) =>
        c.status !== "In Sprint" &&
        c.status !== "Done" &&
        !boardCardIds.has(c.id) &&
        (q === "" || c.title.toLowerCase().includes(q))
    );
  }, [cards, search, boardCardIds]);

  const handleAdd = useCallback(
    async (card: FeatureCard) => {
      if (!sprintId) return;
      setAddingId(card.id);
      try {
        await sprintsApi.addItem(sprintId, {
          card_id: card.id,
          kanban_column: "Candidate Pool",
          priority_in_sprint: 50,
        });
        onAdded(card.title);
      } catch {
        // error is surfaced via the parent snackbar if needed
      } finally {
        setAddingId(null);
      }
    },
    [sprintId, onAdded]
  );

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 500, fontSize: "0.875rem" }}>
          Backlog
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close backlog panel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search…"
        fullWidth
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ mx: 1.5, my: 1, width: "calc(100% - 24px)" }}
      />

      {/* Card list */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {visibleCards.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 1.5, py: 2, opacity: 0.6 }}
          >
            {search.trim() ? "No matching cards" : "No backlog cards available"}
          </Typography>
        )}
        {visibleCards.map((card) => (
          <Tooltip
            key={card.id}
            placement="left"
            enterDelay={400}
            title={
              <Box sx={{ maxWidth: 260 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: card.description ? 0.5 : 0 }}>
                  {card.title}
                </Typography>
                {card.description && (
                  <>
                    <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.15)", mb: 0.5 }} />
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block", lineHeight: 1.5 }}>
                      {card.description.length > 180 ? `${card.description.slice(0, 180)}…` : card.description}
                    </Typography>
                  </>
                )}
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: "default",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Box sx={{ pt: "2px", flexShrink: 0 }}>
                <PriorityDot priority={card.priority} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
              >
                {card.title}
              </Typography>
              <IconButton
                size="small"
                disabled={addingId === card.id}
                onClick={() => void handleAdd(card)}
                aria-label={`Add "${card.title}" to sprint`}
                sx={{ flexShrink: 0, p: "2px", mt: "-2px" }}
              >
                {addingId === card.id ? (
                  <CircularProgress size={14} />
                ) : (
                  <AddIcon sx={{ fontSize: "1rem" }} />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

// ── CreateSprintDialog ────────────────────────────────────────

interface CreateSprintDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (newSprintId: number) => void;
}

const MAX_SPRINT_NAME_LENGTH = 20;

function CreateSprintDialog({
  open,
  onClose,
  onCreated,
}: CreateSprintDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setErrorMessage(null);
      setSubmitting(false);
    }
  }, [open]);

  const nameError = name.trim().length === 0
    ? null // only show error after attempted submit
    : name.length > MAX_SPRINT_NAME_LENGTH
      ? `Name must be ${MAX_SPRINT_NAME_LENGTH} characters or fewer`
      : null;

  const handleCreate = useCallback(async () => {
    if (!name.trim()) {
      setErrorMessage("Sprint name is required");
      return;
    }
    if (name.length > MAX_SPRINT_NAME_LENGTH) {
      setErrorMessage(`Sprint name must be ${MAX_SPRINT_NAME_LENGTH} characters or fewer`);
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await sprintsApi.create({
        name: name.trim(),
        description: description.trim() || null,
      });
      onCreated(created.id);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to create sprint");
      setSubmitting(false);
    }
  }, [name, description, onCreated]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>Create Sprint</DialogTitle>

      <DialogContent sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Sprint Name"
          placeholder="e.g. Maps Editor Core"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errorMessage) setErrorMessage(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && void handleCreate()}
          fullWidth
          required
          disabled={submitting}
          autoFocus
          error={Boolean(nameError)}
          helperText={nameError ?? `${name.length}/${MAX_SPRINT_NAME_LENGTH}`}
          inputProps={{ maxLength: MAX_SPRINT_NAME_LENGTH + 5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  color={name.length > MAX_SPRINT_NAME_LENGTH ? "error" : "text.secondary"}
                >
                  {name.length}/{MAX_SPRINT_NAME_LENGTH}
                </Typography>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Description"
          placeholder="What should this sprint achieve? (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          disabled={submitting}
        />

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 0 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={submitting || !name.trim() || name.length > MAX_SPRINT_NAME_LENGTH}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AddCircleOutlineIcon fontSize="small" />
            )
          }
        >
          {submitting ? "Creating…" : "Create Sprint"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Shared admin password dialog helper ───────────────────────

interface AdminPasswordDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmColor?: "success" | "warning" | "error";
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

function AdminPasswordDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmColor = "success",
  onClose,
  onSubmit,
}: AdminPasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setErrorMessage(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!password.trim()) {
      setErrorMessage("Password is required");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onSubmit(password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      if (
        msg.includes("403") ||
        msg.toLowerCase().includes("forbidden") ||
        msg.toLowerCase().includes("incorrect") ||
        msg.toLowerCase().includes("invalid")
      ) {
        setErrorMessage("Incorrect password");
      } else {
        setErrorMessage(msg);
      }
      setSubmitting(false);
    }
  }, [password, onSubmit]);

  const colorSx =
    confirmColor === "success"
      ? {
          backgroundColor: "success.main",
          color: "#fff",
          "&:hover": { backgroundColor: "success.dark" },
          "&.Mui-disabled": {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
          },
        }
      : confirmColor === "warning"
      ? {
          backgroundColor: "warning.main",
          color: "#fff",
          "&:hover": { backgroundColor: "warning.dark" },
          "&.Mui-disabled": {
            backgroundColor: "action.disabledBackground",
            color: "action.disabled",
          },
        }
      : {};

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        <TextField
          inputRef={inputRef}
          label="Admin Password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errorMessage) setErrorMessage(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !submitting) void handleSubmit();
          }}
          fullWidth
          autoFocus
          disabled={submitting}
          error={Boolean(errorMessage)}
          helperText={errorMessage ?? " "}
          autoComplete="current-password"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || !password.trim()}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon fontSize="small" />
            )
          }
          sx={colorSx}
        >
          {submitting ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// The Queue / Un-queue / Start sprint dialog wrappers used to live here.
// Greg's 2026-05-15 policy dropped the admin-password gate on these
// transitions, so the wrappers became one-line shells around a
// now-unused password modal. They were removed; the header buttons in
// `KanbanView` call `sprintsApi.confirm` / `start` / `unqueue` directly
// via `runSprintTransition`. `AdminPasswordDialog` (above) is retained
// for potential future use (e.g. Danger Zone wiring).

// ── DragOverlay card ──────────────────────────────────────────

function KanbanCardDragOverlay({ card }: { card: FeatureCard }) {
  return (
    <Paper
      sx={{
        p: 1,
        maxWidth: 280,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        rotate: "2deg",
        opacity: 0.95,
      }}
      elevation={8}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
        {card.title}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
        <PriorityBadge priority={card.priority ?? "Medium"} />
      </Box>
    </Paper>
  );
}
